import { AxiosError, AxiosHeaders } from 'axios'
import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

import { Dispatch, inOperator } from './typeHelpers'
import { userLogout } from '../reducers/rootReducer.actions'
import { isAdminContext, redirectToAdminLogin } from './adminContext'
import { X_IS_ADMIN_CLIENT_HEADER } from '../api'

export const logHeardError = (error: unknown, context?: object) => {
  if (typeof error === 'string') {
    datadogRum.addError(new Error(error), context)
  } else {
    datadogRum.addError(error, context)
  }
}

export const DEFAULT_ERROR = 'Something went wrong. Please try again.'

export function isAxiosError(error: unknown): error is AxiosError {
  return error !== null && typeof error === 'object' && 'isAxiosError' in error
}

export function isStandardError(error: unknown): error is Error {
  return (
    error !== null &&
    typeof error === 'object' &&
    'name' in error &&
    'message' in error
  )
}

// Dig through response and look for message.  Important for Gusto errors
const parseMessageFromData = (data: unknown): string | null => {
  if (Array.isArray(data)) {
    for (const item of data) {
      const message = parseMessageFromData(item)
      if (message) {
        return message
      }
    }
  } else if (typeof data === 'object' && data) {
    if (
      inOperator('full_message', data) &&
      typeof data.full_message === 'string'
    ) {
      return data.full_message
    }
    if (inOperator('message', data) && typeof data.message === 'string') {
      return data.message
    }
    if (
      inOperator('errors', data) &&
      Array.isArray(data.errors) &&
      typeof data.errors[0] === 'string'
    ) {
      return data.errors[0]
    }
    for (const item of Object.values(data)) {
      const message = parseMessageFromData(item)
      if (message) {
        return message
      }
    }
  }
  return null
}

const isMarkup = (body: string): boolean => body.trimStart().startsWith('<')

export function parseErrorFromCatch(
  err: unknown,
  defaultError = DEFAULT_ERROR
): string {
  if (isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK') {
      return 'Request timed out. Please try again.'
    }

    if (err.message === 'Network Error' && !err.response) {
      return 'Unable to connect. Please check your connection and try again.'
    }
  }

  if (
    isAxiosError(err) &&
    err.response?.data &&
    typeof err.response?.data === 'object'
  ) {
    const messageFromData = parseMessageFromData(err.response.data)

    if (messageFromData) {
      return messageFromData
    } else if (
      inOperator('error_message', err.response.data) &&
      typeof err.response.data.error_message === 'string'
    ) {
      return err.response.data.error_message
    } else {
      return defaultError
    }
  } else if (
    isAxiosError(err) &&
    err.response?.data &&
    typeof err.response?.data === 'string'
  ) {
    return isMarkup(err.response.data) ? defaultError : err.response.data
  } else if (isStandardError(err)) {
    return err.message
  } else if (typeof err === 'string') {
    return err
  } else {
    return defaultError
  }
}

const isAdminApiError = (error: AxiosError): boolean => {
  const headers = error.config?.headers
  return (
    headers instanceof AxiosHeaders &&
    headers.get(X_IS_ADMIN_CLIENT_HEADER) === 'true'
  )
}

export const handleApiError = (error: unknown) => (dispatch: Dispatch) => {
  // If we get a 401 session has expired.  These should not be logged
  if (isAxiosError(error) && error.response?.status === 401) {
    if (isAdminApiError(error)) {
      redirectToAdminLogin()
    } else if (isAdminContext()) {
      // An admin browsing an admin page has no customer (Passport) session, so
      // 401s from incidental customer-endpoint fetches are expected. Logging
      // out or redirecting here would bounce the admin on every such fetch;
      // real admin-session loss surfaces as an admin-client 401 above. We still
      // report the occurrence to Datadog (which customer endpoint fired in
      // admin context) so the remaining commingling can be found and gated off.
      datadogLogs.logger.warn(
        'Customer-endpoint 401 in admin context (no-op)',
        {
          url: error.config?.url,
          method: error.config?.method,
        }
      )
    } else {
      dispatch(userLogout())
    }
  } else {
    logHeardError(error)
  }
}

/** Lets callers branch on a specific failure instead of on display copy. */
export function parseErrorCodeFromCatch(err: unknown): string | undefined {
  if (!isAxiosError(err)) return undefined

  const data: unknown = err.response?.data
  if (!data || typeof data !== 'object' || !('errorCode' in data)) {
    return undefined
  }

  const { errorCode } = data
  return typeof errorCode === 'string' ? errorCode : undefined
}
